import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCheck, KeyRound, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type Step = "email" | "code" | "reset";

export default function ForgotPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({ title: "Email requis", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error("Erreur d’envoi");
      toast({ title: "Code envoyé", description: "Vérifiez votre boîte e-mail." });
      setStep("code");
    } catch (err: any) {
      toast({ title: "Échec", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = code.replace(/\D/g, "").slice(0, 6);
    if (!/^[0-9]{6}$/.test(c)) {
      toast({ title: "Code invalide", description: "Entrez 6 chiffres.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim(), code: c }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.reset_token) throw new Error(data?.detail || "Code invalide");
      setResetToken(data.reset_token);
      toast({ title: "Code vérifié ✅" });
      setStep("reset");
    } catch (err: any) {
      toast({ title: "Échec", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd1.length < 6) {
      toast({ title: "Mot de passe trop court", description: "Minimum 6 caractères.", variant: "destructive" });
      return;
    }
    if (pwd1 !== pwd2) {
      toast({
        title: "Confirmation incorrecte",
        description: "Les deux mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ reset_token: resetToken, new_password: pwd1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.access_token) throw new Error(data?.detail || "Erreur de réinitialisation");

      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("auth_user", JSON.stringify(data.user || {}));
      localStorage.setItem("must_verify_code", "0");

      // maj contexte
      login({
        email: data.user?.email,
        name: data.user?.name,
        role: data.user?.role,
        mustChangePassword: !!data.user?.must_change_password,
      });

      toast({ title: "Mot de passe mis à jour" });
      navigate("/dashboard");
    } catch (err: any) {
      toast({ title: "Échec", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background flou (même que Login) */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: "ur[](https://images.unsplash.com/photo-1557682250-33bd709cbe85?auto=format&fit=crop&w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(10px)",
          transform: "scale(1.08)",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />

      {/* Carte compacte */}
      <div className="relative w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-300">
        <div className="rounded-3xl shadow-2xl backdrop-blur-xl border border-white/20 bg-white/30 dark:bg-black/30">
          <div className="p-6 sm:p-7 space-y-6">
            <button
              onClick={() => navigate("/")}
              className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              type="button"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour à la connexion
            </button>

            {step === "email" && (
              <>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <h1 className="text-xl font-semibold">Mot de passe oublié</h1>
                </div>
                <p className="text-sm text-muted-foreground">
                  Entrez votre e-mail. Nous vous enverrons un code de vérification.
                </p>
                <form onSubmit={sendEmail} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                      className="bg-background/60"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={busy}>
                    {busy ? "Envoi…" : "Envoyer le code"}
                  </Button>
                </form>
              </>
            )}

            {step === "code" && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCheck className="h-5 w-5 text-primary" />
                  <h1 className="text-xl font-semibold">Vérifier le code</h1>
                </div>
                <p className="text-sm text-muted-foreground">Saisissez le code à 6 chiffres reçu par e-mail.</p>
                <form onSubmit={verifyCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      title="Entrez 6 chiffres"
                      placeholder="000000"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      className="bg-background/60"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={busy}>
                    {busy ? "Vérification…" : "Vérifier"}
                  </Button>
                </form>
              </>
            )}

            {step === "reset" && (
              <>
                <div className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-primary" />
                  <h1 className="text-xl font-semibold">Nouveau mot de passe</h1>
                </div>
                <form onSubmit={doReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="p1">Mot de passe</Label>
                    <Input
                      id="p1"
                      type="password"
                      value={pwd1}
                      onChange={(e) => setPwd1(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="bg-background/60"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="p2">Confirmer</Label>
                    <Input
                      id="p2"
                      type="password"
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="bg-background/60"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={busy}>
                    {busy ? "Enregistrement…" : "Réinitialiser"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
